import type { MapService, MapServiceConfig, Restaurant, Coordinates, MapRenderOptions } from '../types.ts'
import { debugLog } from '../../../applications/web/sources/components/DebugPanel.tsx'

declare global {
  interface Window {
    kakao: any
  }
}

export class KakaoMapService implements MapService {
  private placesService: any
  private isLoaded = false
  private markers: any[] = []
  private markerMap: Map<string, any> = new Map()

  constructor(private config: MapServiceConfig) {
    debugLog('info', 'KakaoMapService initialized', { config })
  }

  private async loadScript(): Promise<void> {
    if (this.isLoaded) return

    debugLog('info', 'Loading Kakao Maps script...')

    return new Promise((resolve, reject) => {
      if (window.kakao?.maps) {
        debugLog('info', 'Kakao Maps already loaded')
        this.isLoaded = true
        resolve()
        return
      }

      const script = document.createElement('script')
      const scriptUrl = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${this.config.apiKey}&libraries=services&autoload=false`
      script.src = scriptUrl
      script.async = true
      
      debugLog('info', 'Loading Kakao Maps script from URL:', scriptUrl)
      
      script.onload = () => {
        debugLog('info', 'Kakao Maps script loaded, initializing...')
        window.kakao.maps.load(() => {
          this.placesService = new window.kakao.maps.services.Places()
          this.isLoaded = true
          debugLog('info', 'Kakao Maps initialized successfully')
          resolve()
        })
      }
      
      script.onerror = (error) => {
        debugLog('error', 'Failed to load Kakao Maps script', { error, scriptUrl, apiKey: this.config.apiKey })
        reject(new Error(`Failed to load Kakao Maps script from ${scriptUrl}`))
      }
      document.head.appendChild(script)
    })
  }

  async searchNearby(coordinates: Coordinates, radius: number): Promise<Restaurant[]> {
    debugLog('info', 'Searching nearby restaurants', { coordinates, radius })
    
    try {
      await this.loadScript()
    } catch (error) {
      debugLog('error', 'Failed to load Kakao Maps', error)
      // 개발 환경에서 mock 데이터 반환
      if (import.meta.env.DEV) {
        debugLog('warn', 'Returning mock data due to Kakao Maps loading failure')
        return this.getMockRestaurants(coordinates)
      }
      throw error
    }

    return new Promise((resolve, reject) => {
      const callback = (result: any, status: any) => {
        debugLog('info', 'Kakao search callback', { status, resultCount: result?.length })
        
        if (status === window.kakao.maps.services.Status.OK) {
          debugLog('info', 'Search successful', { totalResults: result.length })
          const restaurants = result
            .filter((place: any) => place.category_group_code === 'FD6' || place.category_name.includes('음식점'))
            .map((place: any) => this.mapKakaoToRestaurant(place, coordinates))
          debugLog('info', 'Filtered restaurants', { count: restaurants.length })
          resolve(restaurants)
        } else if (status === window.kakao.maps.services.Status.ZERO_RESULT) {
          debugLog('warn', 'No results found')
          resolve([])
        } else {
          debugLog('error', 'Kakao Places search failed', { status })
          reject(new Error(`Kakao Places search failed: ${status}`))
        }
      }

      const searchOptions = {
        location: new window.kakao.maps.LatLng(coordinates.latitude, coordinates.longitude),
        radius: radius,
        sort: window.kakao.maps.services.SortBy.DISTANCE
      }
      
      debugLog('info', 'Starting category search', { category: 'FD6', options: searchOptions })
      
      try {
        this.placesService.categorySearch('FD6', callback, searchOptions)
      } catch (error) {
        debugLog('error', 'Category search error', error)
        reject(error)
      }
    })
  }

  async searchByKeyword(keyword: string, coordinates?: Coordinates): Promise<Restaurant[]> {
    await this.loadScript()

    return new Promise((resolve, reject) => {
      const options: any = {}
      if (coordinates) {
        options.location = new window.kakao.maps.LatLng(coordinates.latitude, coordinates.longitude)
        options.sort = window.kakao.maps.services.SortBy.DISTANCE
      }

      const callback = (result: any, status: any) => {
        if (status === window.kakao.maps.services.Status.OK) {
          const restaurants = result
            .filter((place: any) => place.category_group_code === 'FD6' || place.category_name.includes('음식점'))
            .map((place: any) => this.mapKakaoToRestaurant(place, coordinates))
          resolve(restaurants)
        } else if (status === window.kakao.maps.services.Status.ZERO_RESULT) {
          resolve([])
        } else {
          reject(new Error('Kakao keyword search failed'))
        }
      }

      this.placesService.keywordSearch(keyword + ' 음식점', callback, options)
    })
  }

  async getRestaurantDetail(_placeId: string): Promise<Restaurant | null> {
    // Kakao doesn't have a direct detail API in JS SDK
    // Would need to use REST API for more details
    return null
  }

  async renderMap(container: HTMLElement, coordinates: Coordinates, options?: MapRenderOptions): Promise<any> {
    debugLog('info', 'Rendering Kakao map', { coordinates, options })

    try {
      await this.loadScript()
    } catch (error) {
      debugLog('error', 'Failed to load Kakao Maps for rendering', error)
      // 개발 환경에서는 에러를 무시하고 빈 맵 컨테이너 반환
      if (import.meta.env.DEV) {
        debugLog('warn', 'Skipping map rendering due to Kakao Maps loading failure')
        return null
      }
      throw error
    }

    const mapOptions = {
      center: new window.kakao.maps.LatLng(coordinates.latitude, coordinates.longitude),
      level: options?.zoom || 3
    }

    const map = new window.kakao.maps.Map(container, mapOptions)

    // 기존 마커 배열 초기화 (새 지도이므로)
    this.markers = []
    this.markerMap.clear()

    // 현재 위치 마커 추가
    const currentMarker = new window.kakao.maps.Marker({
      position: new window.kakao.maps.LatLng(coordinates.latitude, coordinates.longitude),
      map: map
    })
    this.markers.push(currentMarker)

    // 추가 마커들 표시
    if (options?.markers) {
      options.markers.forEach(restaurant => {
        const restaurantMarker = new window.kakao.maps.Marker({
          position: new window.kakao.maps.LatLng(
            restaurant.coordinates.latitude,
            restaurant.coordinates.longitude
          ),
          map: map
        })

        // 인포윈도우 추가
        const infowindow = new window.kakao.maps.InfoWindow({
          content: `<div style="padding:5px;font-size:12px;">${restaurant.name}</div>`
        })

        window.kakao.maps.event.addListener(restaurantMarker, 'click', () => {
          infowindow.open(map, restaurantMarker)
        })

        this.markers.push(restaurantMarker)
        this.markerMap.set(restaurant.id, { marker: restaurantMarker, infowindow })
      })
    }

    // 지도 이벤트 등록
    if (options?.onMapMove) {
      window.kakao.maps.event.addListener(map, 'dragend', () => {
        debugLog('info', 'Map dragend event triggered')
        options.onMapMove?.()
      })

      window.kakao.maps.event.addListener(map, 'zoom_changed', () => {
        debugLog('info', 'Map zoom_changed event triggered')
        options.onMapMove?.()
      })

      debugLog('info', 'Map event listeners registered')
    }

    debugLog('info', 'Kakao map rendered successfully')
    return map
  }

  getMapCenter(mapInstance: any): Coordinates | null {
    if (!mapInstance) return null

    try {
      const center = mapInstance.getCenter()
      return {
        latitude: center.getLat(),
        longitude: center.getLng()
      }
    } catch (error) {
      debugLog('error', 'Failed to get map center', error)
      return null
    }
  }

  updateMarkers(mapInstance: any, restaurants: Restaurant[]): void {
    debugLog('info', 'Updating markers', { count: restaurants.length })

    // 기존 마커 제거
    this.markers.forEach(marker => {
      marker.setMap(null)
    })
    this.markers = []
    this.markerMap.clear()

    // 새 마커 추가
    restaurants.forEach(restaurant => {
      const marker = new window.kakao.maps.Marker({
        position: new window.kakao.maps.LatLng(
          restaurant.coordinates.latitude,
          restaurant.coordinates.longitude
        ),
        map: mapInstance
      })

      // 인포윈도우 추가
      const infowindow = new window.kakao.maps.InfoWindow({
        content: `<div style="padding:5px;font-size:12px;">${restaurant.name}</div>`
      })

      window.kakao.maps.event.addListener(marker, 'click', () => {
        infowindow.open(mapInstance, marker)
      })

      this.markers.push(marker)
      this.markerMap.set(restaurant.id, { marker, infowindow })
    })

    debugLog('info', 'Markers updated successfully', { totalMarkers: this.markers.length })
  }

  highlightMarker(mapInstance: any, restaurantId: string): void {
    debugLog('info', 'Highlighting marker', { restaurantId })

    const markerData = this.markerMap.get(restaurantId)
    if (!markerData) {
      debugLog('warn', 'Marker not found for restaurant', { restaurantId })
      return
    }

    const { marker, infowindow } = markerData

    // 모든 인포윈도우 닫기
    this.markerMap.forEach(data => {
      data.infowindow.close()
    })

    // 선택된 마커의 인포윈도우 열기
    infowindow.open(mapInstance, marker)

    // 마커에 애니메이션 효과 추가 (bounce)
    marker.setZIndex(1000)

    debugLog('info', 'Marker highlighted successfully', { restaurantId })
  }

  private mapKakaoToRestaurant(place: any, userCoordinates?: Coordinates): Restaurant {
    const restaurant: Restaurant = {
      id: place.id,
      name: place.place_name,
      address: place.road_address_name || place.address_name,
      coordinates: {
        latitude: parseFloat(place.y),
        longitude: parseFloat(place.x)
      },
      phone: place.phone,
      category: place.category_name,
      platformData: {
        platform: 'kakao',
        placeId: place.id,
        url: place.place_url
      }
    }

    if (userCoordinates && place.distance) {
      restaurant.distance = parseInt(place.distance)
    }

    return restaurant
  }

  private getMockRestaurants(userCoordinates: Coordinates): Restaurant[] {
    // 개발용 mock 데이터
    const mockRestaurants = [
      {
        id: 'mock-1',
        name: '테스트 김치찌개',
        address: '서울특별시 강남구 테헤란로 123',
        coordinates: {
          latitude: userCoordinates.latitude + 0.001,
          longitude: userCoordinates.longitude + 0.001
        },
        phone: '02-1234-5678',
        category: '한식 > 김치찌개',
        distance: 120,
        platformData: {
          platform: 'kakao' as const,
          placeId: 'mock-1'
        }
      },
      {
        id: 'mock-2',
        name: '맛있는 피자',
        address: '서울특별시 강남구 역삼로 456',
        coordinates: {
          latitude: userCoordinates.latitude - 0.001,
          longitude: userCoordinates.longitude + 0.002
        },
        phone: '02-2345-6789',
        category: '양식 > 피자',
        distance: 250,
        platformData: {
          platform: 'kakao' as const,
          placeId: 'mock-2'
        }
      },
      {
        id: 'mock-3',
        name: '행복한 중국집',
        address: '서울특별시 강남구 도곡로 789',
        coordinates: {
          latitude: userCoordinates.latitude + 0.002,
          longitude: userCoordinates.longitude - 0.001
        },
        phone: '02-3456-7890',
        category: '중식 > 중국요리',
        distance: 350,
        platformData: {
          platform: 'kakao' as const,
          placeId: 'mock-3'
        }
      }
    ]
    
    return mockRestaurants
  }
}